import {
  KPI_GROUP_IDS,
  KpiDetailRow,
  KpiDirection,
  KpiGroupId,
  KpiIds,
  KpiModel,
  RawKpi,
  arrayOfStrKpis,
  isDurationKpi,
  keyValueMapKpis,
  kpiDetailRows,
  kpiDirectionOf,
  kpiGroupOf,
  kpiMapEntries,
  partOfTotalMapKpis,
  sortKpiIds
} from '@core/models/kpi.model';
import { formatQuantity } from '@core/utils/quantity-utils';
import { formatDuration, fromSecondsToDuration } from '@core/utils/time-utils';

import { SelectItem } from '@shared/components/select/select-item.model';

/** How a cell states its value: as itself, or against the reference column. */
export type KpiCompareMode = 'abs' | 'delta' | 'pct';

/** Whether a delta moved the KPI the way its direction calls better. */
export type KpiTone = 'good' | 'bad' | 'flat';

/** Column colours, in assignment order; the tokens live in the global stylesheet. */
export const KPI_COMPARE_COLORS: string[] = Array.from(
  { length: 8 },
  (_, index) => `var(--kpi-compare-${index + 1})`
);

export interface KpiComparisonItem {
  readonly id: string;
  readonly label: string;
  readonly sublabel?: string;
  readonly color: string;
  readonly kpis: KpiModel[];
  /** What the column can be switched to; the header shows a picker when there are two or more. */
  readonly choices?: SelectItem[];
  readonly choiceId?: string;
}

export interface KpiComparisonChoice {
  readonly itemId: string;
  readonly choiceId: string;
}

export interface KpiComparisonCell {
  readonly itemId: string;
  /** The column's colour, so a value bar reads as belonging to its column. */
  readonly color: string;
  /** The value in `abs`, the signed delta in `delta`/`pct`, an em dash when the item has no such KPI. */
  readonly text: string;
  /** Unit affixes, rendered only in `abs` — a delta carries its unit inside `text`. */
  readonly prefix: string;
  readonly suffix: string;
  readonly present: boolean;
  readonly isReference: boolean;
  readonly isBest: boolean;
  readonly isWorst: boolean;
  /** Null outside `delta`/`pct`, on the reference column, and on a KPI with no direction. */
  readonly tone: KpiTone | null;
  /** Bar length, 0..1. Against the row's largest magnitude in `abs`, the largest shift otherwise. */
  readonly bar: number;
  /** True when the bar grows out from the centre (a delta) rather than from the left (a value). */
  readonly barDiverging: boolean;
  /** Which side of the centre a diverging bar occupies. Meaningless unless `barDiverging`. */
  readonly barPositive: boolean;
  /** The full list behind a map or array KPI's summary cell. Empty everywhere else. */
  readonly detail: readonly KpiDetailRow[];
}

export interface KpiComparisonRow {
  readonly key: string;
  readonly kpiId: KpiIds;
  readonly labelKey: string;
  /** Present on a map KPI's per-key sub-row, absent on the KPI's own row. */
  readonly mapKey?: string;
  readonly direction: KpiDirection;
  readonly depth: 0 | 1;
  readonly cells: KpiComparisonCell[];
}

export interface KpiComparisonGroup {
  readonly id: KpiGroupId;
  readonly labelKey: string;
  readonly rows: KpiComparisonRow[];
}

export interface KpiComparisonOptions {
  readonly mode: KpiCompareMode;
  readonly referenceId: string | null;
  readonly decimals: number;
  /** The user's stored KPI order; rows follow it, then the KPIs' declaration order. */
  readonly kpiOrder: KpiIds[];
}

const EM_DASH = '—';
/** U+2212, the minus that lines up with `+` at the same width. */
const MINUS = '−';

/**
 * Turns a list row's `kpis` field into `KpiModel`s, skipping entries without an id so one bad
 * row cannot empty the whole panel.
 */
export function normalizeRawKpis(
  value: RawKpi[] | undefined,
  decimals: number
): KpiModel[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((entry) => typeof entry?.id === 'string' && entry.id.length > 0)
    .map((entry) => new KpiModel(entry, decimals));
}

/**
 * The smallest step a KPI's displayed value can show: whole seconds for a duration, which
 * `formatDuration` rounds to, and the decimals preference for everything else.
 */
export function displayResolution(kpiId: KpiIds, decimals: number): number {
  return isDurationKpi(kpiId) ? 1 : Math.pow(10, -decimals);
}

/** The value as it reads on screen, so two values that print the same compare equal. */
function quantize(value: number, resolution: number): number {
  return Math.round(value / resolution);
}

/** Whether a difference rounds away at the displayed resolution. */
function isNegligible(diff: number, resolution: number): boolean {
  return Math.abs(diff) < resolution / 2;
}

/**
 * The marker for a difference too small to print: `=` only when the values really are equal,
 * `~` when a non-zero difference rounds away, so a rounded zero never passes for a tie.
 */
function negligibleMarker(diff: number, resolution: number): string | null {
  if (diff === 0) return '=';
  return isNegligible(diff, resolution) ? '~' : null;
}

export function formatKpiDelta(
  kpiId: KpiIds,
  diff: number,
  affixes: { prefix?: string; suffix?: string },
  decimals: number
): string {
  const negligible = negligibleMarker(diff, displayResolution(kpiId, decimals));
  if (negligible) return negligible;
  const sign = diff > 0 ? '+' : MINUS;
  const magnitude = isDurationKpi(kpiId)
    ? formatDuration(fromSecondsToDuration(Math.abs(diff)))
    : `${affixes.prefix ?? ''}${formatQuantity(Math.abs(diff), decimals)}${affixes.suffix ?? ''}`;
  return `${sign}${magnitude}`;
}

/**
 * The change from `reference` to `value` in percent, signed like `value - reference` whatever
 * the reference's sign. Null for a zero reference, which no percentage can describe.
 */
function percentChange(value: number, reference: number): number | null {
  if (reference === 0) return null;
  return ((value - reference) / Math.abs(reference)) * 100;
}

export function formatKpiPercent(
  value: number,
  reference: number,
  decimals: number
): string {
  const pct = percentChange(value, reference);
  if (pct === null) return EM_DASH;
  const negligible = negligibleMarker(pct, Math.pow(10, -decimals));
  if (negligible) return negligible;
  return `${pct > 0 ? '+' : MINUS}${formatQuantity(Math.abs(pct), decimals)}%`;
}

/** `resolution` is the step the shift is printed at, so a shift that prints as `~` is flat. */
export function kpiTone(
  direction: KpiDirection,
  shift: number,
  resolution: number
): KpiTone | null {
  if (direction === 0) return null;
  if (isNegligible(shift, resolution)) return 'flat';
  return Math.sign(shift) === direction ? 'good' : 'bad';
}

interface RowStats {
  /** Quantized, so they compare against `quantize(value)` rather than the raw value. */
  readonly best: number | null;
  readonly worst: number | null;
  /** Largest absolute value in the row, the denominator for an `abs` bar. */
  readonly span: number;
}

function statsFor(
  values: (number | null)[],
  direction: KpiDirection,
  resolution: number
): RowStats {
  const present = values.filter((v): v is number => v !== null);
  if (present.length === 0) return { best: null, worst: null, span: 0 };

  const shown = present.map((v) => quantize(v, resolution));
  const min = Math.min(...shown);
  const max = Math.max(...shown);
  const span = Math.max(...present.map(Math.abs));
  if (direction === 0 || min === max) return { best: null, worst: null, span };
  return direction > 0 ? { best: max, worst: min, span } : { best: min, worst: max, span };
}

function buildScalarRow(
  kpiId: KpiIds,
  items: KpiComparisonItem[],
  byItem: Map<string, KpiModel>[],
  options: KpiComparisonOptions
): KpiComparisonRow {
  const { mode, referenceId, decimals } = options;
  const direction = kpiDirectionOf(kpiId);
  const models = byItem.map((map) => map.get(kpiId) ?? null);
  const values = models.map((kpi) => kpi?.numericValue ?? null);
  const resolution = displayResolution(kpiId, decimals);

  const stats = statsFor(values, direction, resolution);
  const referenceIndex = items.findIndex((item) => item.id === referenceId);
  const referenceValue = referenceIndex >= 0 ? values[referenceIndex] : null;
  const comparing = mode !== 'abs' && referenceValue !== null;

  // What a non-reference cell prints, as a number: the delta, or the percentage in `pct`.
  // Tone and bar read it rather than the raw delta, so neither contradicts the text.
  const shiftResolution = mode === 'pct' ? Math.pow(10, -decimals) : resolution;
  const shifts = values.map((v) => {
    if (!comparing || v === null) return null;
    const ref = referenceValue as number;
    return mode === 'pct' ? percentChange(v, ref) : v - ref;
  });
  const shiftSpan = Math.max(
    ...shifts.map((d) => (d === null ? 0 : Math.abs(d))),
    Number.MIN_VALUE
  );

  const cells = items.map((item, index): KpiComparisonCell => {
    const kpi = models[index];
    const value = values[index];
    const isReference = item.id === referenceId;

    if (!kpi) {
      return {
        itemId: item.id,
        color: item.color,
        text: EM_DASH,
        prefix: '',
        suffix: '',
        present: false,
        isReference,
        isBest: false,
        isWorst: false,
        tone: null,
        bar: 0,
        barDiverging: false,
        barPositive: false,
        detail: []
      };
    }

    const affixes = kpi.metadata;
    const diverging = comparing && !isReference && value !== null;
    const shift = diverging ? shifts[index] : null;

    let text: string;
    if (!diverging) {
      text = kpi.value;
    } else if (mode === 'pct') {
      text = formatKpiPercent(value as number, referenceValue as number, decimals);
    } else {
      text = formatKpiDelta(kpiId, shift as number, affixes, decimals);
    }
    const shown = value === null ? null : quantize(value, resolution);

    return {
      itemId: item.id,
      color: item.color,
      text,
      // A delta already carries its unit; only a plain value needs the affixes beside it.
      prefix: diverging ? '' : (affixes.prefix ?? ''),
      suffix: diverging ? '' : (affixes.suffix ?? ''),
      present: true,
      isReference,
      isBest: shown !== null && shown === stats.best,
      isWorst: shown !== null && shown === stats.worst,
      tone: shift === null ? null : kpiTone(direction, shift, shiftResolution),
      bar: diverging
        ? shift === null
          ? 0
          : Math.min(1, Math.abs(shift) / shiftSpan)
        : value === null || stats.span === 0
          ? 0
          : Math.max(0.015, Math.abs(value) / stats.span),
      barDiverging: diverging,
      barPositive: (shift ?? 0) >= 0,
      detail: []
    };
  });

  return {
    key: kpiId,
    kpiId,
    labelKey: `kpis.${kpiId}`,
    direction,
    depth: 0,
    cells
  };
}

function textCell(
  item: KpiComparisonItem,
  text: string,
  detail: readonly KpiDetailRow[] = []
): KpiComparisonCell {
  return {
    itemId: item.id,
    color: item.color,
    text,
    prefix: '',
    suffix: '',
    present: text !== EM_DASH,
    isReference: false,
    isBest: false,
    isWorst: false,
    tone: null,
    bar: 0,
    barDiverging: false,
    barPositive: false,
    detail
  };
}

/**
 * A map KPI's own row (a per-item summary) followed by one sub-row per key, over the union of
 * the keys every item reports. Sub-rows carry no delta or ranking: `getMapEntries` hands back
 * strings already formatted for display, and there is no raw accessor to do arithmetic on.
 */
function buildMapRows(
  kpiId: KpiIds,
  items: KpiComparisonItem[],
  byItem: Map<string, KpiModel>[],
  referenceId: string | null
): KpiComparisonRow[] {
  const models = byItem.map((map) => map.get(kpiId) ?? null);

  const perItem = models.map(
    (kpi) =>
      new Map<string, string>(kpi ? kpiMapEntries(kpi).map((e) => [e.key, e.text]) : [])
  );

  const mapKeys: string[] = [];
  for (const entries of perItem) {
    for (const key of entries.keys()) {
      if (!mapKeys.includes(key)) mapKeys.push(key);
    }
  }

  const summary: KpiComparisonRow = {
    key: kpiId,
    kpiId,
    labelKey: `kpis.${kpiId}`,
    direction: 0,
    depth: 0,
    cells: items.map((item, index) => {
      const kpi = models[index];
      const cell = textCell(item, kpi ? kpi.value : EM_DASH, kpi ? kpiDetailRows(kpi) : []);
      return { ...cell, isReference: item.id === referenceId };
    })
  };

  const subRows = mapKeys.map(
    (mapKey): KpiComparisonRow => ({
      key: `${kpiId}:${mapKey}`,
      kpiId,
      labelKey: `kpis.${kpiId}`,
      mapKey,
      direction: 0,
      depth: 1,
      cells: items.map((item, index) => {
        const cell = textCell(item, perItem[index].get(mapKey) ?? EM_DASH);
        return { ...cell, isReference: item.id === referenceId };
      })
    })
  );

  return [summary, ...subRows];
}

/**
 * An array KPI compares as its length — the ids themselves are a set, and set membership is a
 * different question from "which plan scores better". The full list stays in the detail card.
 */
function buildArrayRow(
  kpiId: KpiIds,
  items: KpiComparisonItem[],
  byItem: Map<string, KpiModel>[],
  referenceId: string | null
): KpiComparisonRow {
  const models = byItem.map((map) => map.get(kpiId) ?? null);
  return {
    key: kpiId,
    kpiId,
    labelKey: `kpis.${kpiId}`,
    direction: 0,
    depth: 0,
    cells: items.map((item, index) => {
      const kpi = models[index];
      const cell = textCell(
        item,
        kpi ? String(kpi.getformattedArray().length) : EM_DASH,
        kpi ? kpiDetailRows(kpi) : []
      );
      return { ...cell, isReference: item.id === referenceId };
    })
  };
}

/**
 * The comparison matrix: KPI rows grouped into sections, every row carrying exactly one cell
 * per item in the items' own order.
 *
 * Rows are the union of the ids the items report, sorted the way the KPI preferences page
 * lists them. The order never depends on which item is leftmost or on the order the backend
 * sent a plan's KPIs in, so ticking rows or switching a column's plan never reshuffles it.
 */
export function buildComparisonGroups(
  items: KpiComparisonItem[],
  options: KpiComparisonOptions
): KpiComparisonGroup[] {
  if (items.length === 0) return [];

  const byItem = items.map(
    (item) => new Map<string, KpiModel>(item.kpis.map((kpi) => [kpi.id, kpi]))
  );

  const reportedIds = new Set<KpiIds>();
  for (const item of items) {
    for (const kpi of item.kpis) reportedIds.add(kpi.id);
  }
  const orderedIds = sortKpiIds([...reportedIds], options.kpiOrder);

  const rowsByGroup = new Map<KpiGroupId, KpiComparisonRow[]>();
  for (const kpiId of orderedIds) {
    const rows =
      keyValueMapKpis.has(kpiId) || partOfTotalMapKpis.has(kpiId)
        ? buildMapRows(kpiId, items, byItem, options.referenceId)
        : arrayOfStrKpis.has(kpiId)
          ? [buildArrayRow(kpiId, items, byItem, options.referenceId)]
          : [buildScalarRow(kpiId, items, byItem, options)];

    const group = kpiGroupOf(kpiId);
    const bucket = rowsByGroup.get(group);
    if (bucket) bucket.push(...rows);
    else rowsByGroup.set(group, [...rows]);
  }

  return KPI_GROUP_IDS.filter((id) => rowsByGroup.has(id)).map((id) => ({
    id,
    labelKey: `features.kpi_comparison.groups.${id}`,
    rows: rowsByGroup.get(id) as KpiComparisonRow[]
  }));
}
